// CountdownTimer.jsx
import { useState, useEffect } from "react";
import styled from "styled-components";
import { getLocalStorage } from "global/utils/safeJsonParse";
import StepperAnimado from "_+_Portal/_-_Autocheckin/components/genericos/StepperAnimado/StepperAnimado";
import { getStrDateFull } from "global/utils/diasData";

/** Contenedor principal */
const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/** Agrupa los textos (fijo + dinámico) */
const Texts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ImgPublicidad = styled.img`
  width: ${({ $isTerminal }) => ($isTerminal ? "800px !important" : "100%")};
  height: auto !important;
  margin-top: 10px;
  padding: ${({ $isTerminal }) => !$isTerminal && "10px"};
`;

/** Texto fijo + tiempo */
const Label = styled.span`
  font-size: ${({ $isTerminal }) => ($isTerminal ? "22px" : "12px")};
  font-weight: 500;
  padding: 12px;
  color: ${({ $isTerminal }) =>
    $isTerminal ? "var(--color-black)" : "var(--color-grey45)"};
`;

/** Helper: convierte "m,decimal" o "m" (minutos) a segundos */
const parseTimeStringToSeconds = (timeStr) =>
  Math.round(parseFloat(timeStr.replace(",", ".")) * 60);

/**
 * Props:
 * - text: etiqueta fija antes del tiempo
 * - timeString: cadena con minutos, p.ej. "1,5", "3", "4,5"
 * - dateLastCheck: fecha de la última verificación (para mostrar debajo)
 * - onComplete: callback cuando llega a 0
 * - textFormatter: función(secondsLeft) → string dinámico
 */
export default function CountdownTimer({
  text,
  timeString,
  dateLastCheck,
  onComplete,
  textFormatter = () => "",
}) {
  const idTerminal = getLocalStorage("idTerminal");
  // Estado interno de segundos
  const [secondsLeft, setSecondsLeft] = useState(
    parseTimeStringToSeconds(timeString),
  );
  const [formattedTimer, setFormattedTimer] = useState(
    parseTimeStringToSeconds(timeString),
  );

  // 1) Reiniciar si cambia timeString
  useEffect(() => {
    setSecondsLeft(parseTimeStringToSeconds(timeString));
  }, [timeString, dateLastCheck]);

  // 2) Cuenta atrás y reinicio automático al completar
  useEffect(() => {
    if (secondsLeft <= 0) {
      onComplete?.();
      // Reiniciar inmediatamente al valor de timeString
      setSecondsLeft(parseTimeStringToSeconds(timeString));
      return;
    }
    const timerId = setTimeout(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [secondsLeft, onComplete, timeString]);

  useEffect(() => {
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    setFormattedTimer(
      `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
    );
  }, [secondsLeft]);

  return (
    <Container>
      <Texts>
        <StepperAnimado />
        <Label $isTerminal={idTerminal}>{text} {secondsLeft > 0 && <>(Tiempo estimado: {formattedTimer ?? "desconocido"})</>}</Label>
        <ImgPublicidad
          $isTerminal={idTerminal}
          src={process.env.PUBLIC_URL + "/assets/images/promocion-min.jpg"}
          alt="promoción"
        />
        <Label $isTitle={false}>
          Última actualización: {getStrDateFull(dateLastCheck)}
        </Label>
      </Texts>
    </Container>
  );
}
